<template>
  <v-dialog
    v-model="dialog"
    width="800"
    @click:outside="closeDialog"
    persistent
  >
    <v-card class="custom-card" rounded="0">
      <v-toolbar flat color="primary" class="white--text">
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.edit") }} {{ $t("memberDataSection.factory") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon class="white--text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="black--text">
        <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
          <v-row>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="buyingStationName">
                {{ $t("memberDataSection.factoryName") }}<span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="formData.buyingStationName"
                placeholder="Enter here"
                outlined
                dense
                :rules="buyingStationNameRules"
                class="custom-field"
                hide-details="auto"
              />
            </div>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="email">
                {{ $t("memberDataSection.primaryEmailAddress") }}<span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="formData.email"
                placeholder="Enter here"
                outlined
                dense
                :rules="emailRules"
                class="custom-field"
                hide-details="auto"
              />
            </div>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="buyingStationNumber">
                {{ $t("memberDataSection.factoryNumber") }}
              </label>
              <v-text-field
                v-model="formData.buyingStationNumber"
                placeholder="Enter here"
                outlined
                dense
                class="custom-field"
                hide-details="auto"
              />
            </div>
          </v-row>

          <v-card-actions class="mt-10" style="padding: 0 60px 20px 60px !important;">
            <v-spacer />
            <v-btn
              @click="closeDialog"
              outlined
              right
              height="44"
              width="116"
              depressed
              color="primary"
            >
              {{ $t("cancel") }}
            </v-btn>
            <v-btn
              @click="submit"
              right
              height="44"
              width="116"
              color="primary"
              :disabled="!valid"
            >
              {{ $t("submit") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import UserService from "@/_services/UserService";
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
  name: "EditUser",
  mixins: [LoadingMixin],
  props: {
    dialogU: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      valid: false,
      formData: {
        buyingStationName: "",
        email: "",
        buyingStationNumber: "",
      },
    };
  },
  computed: {
    dialog() {
      return this.dialogU;
    },
    buyingStationNameRules() {
      return [(v) => !!v || "Buying station name is required"];
    },
    emailRules() {
      return [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ];
    },
  },
  watch: {
    dialogU: {
      handler(newVal) {
        if (newVal) this.syncFromUser();
      },
      immediate: true,
    },
    user: {
      handler() {
        if (this.dialogU) this.syncFromUser();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    syncFromUser() {
      const u = this.user || {};
      this.formData.buyingStationName =
        `${u.firstName || ""} ${u.middleName || ""}`.trim() || u.fullName || u.name || "";
      this.formData.email = u.email || "";
      this.formData.buyingStationNumber = u.id_number ?? u.buyingStationNumber ?? "";
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    closeDialog() {
      this.$emit("closeUpdateUser");
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.startLoading();
      try {
        const u = this.user || {};
        const activation = Array.isArray(u.activation) ? u.activation : [];
        const activation0 = activation[0] || {};
        const membershipTypeId = activation0?.membership_type;
        const membershipAssoc = activation0?.membership_assoc;
        const membershipType = {
          id: typeof membershipTypeId === "object" ? membershipTypeId.id : membershipTypeId,
          membership_duration: membershipAssoc?.membership_duration,
          membership_duration_unit: membershipAssoc?.membership_duration_unit,
          membership_type: membershipAssoc?.membership_type,
        };
        if (!membershipType?.id) {
          this.$emit("appUserUpdated", {
            success: false,
            message: "Membership type is missing for this factory user.",
          });
          return;
        }

        const payload = {
          firstName: this.formData.buyingStationName,
          middleName: u.middleName || "",
          email: this.formData.email,
          countryCode: u.countryId?.dial_code || u.countryCode || "00",
          mobile: u.mobile || u.phoneNumber || "",
          country:
            u.countryId && typeof u.countryId === "object"
              ? u.countryId.name
              : u.countryId || u.country || "Kenya",
          state: u.state || u.stateId || u.county || u.provinceState || "N/A",
          village: u.village || "",
          district: u.district || "",
          city: u.city || "",
          address: u.address || "",
          status: u.active ? "1" : "0",
          userTribe: u.userTribe || "",
          website: u.website || "",
          membershipTypeId: JSON.stringify(membershipType || {}),
          membershipExtendedDays: activation0.membershipExtendedDays,
          membershipExtensionReason: activation0.membershipExtensionReason,
          id_number: this.formData.buyingStationNumber || "",
        };
        if (u.lastName) payload.lastName = u.lastName;

        const res = await UserService.updateAppUser(u.id, payload);
        this.$emit("appUserUpdated", {
          success: res?.success ?? true,
          message: res?.message || "Updated successfully.",
          user: payload,
        });
        this.closeDialog();
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || "Failed to update factory.";
        this.$emit("appUserUpdated", { success: false, message });
      } finally {
        this.stopLoading();
      }
    },
  },
};
</script>

<style scoped>
.custom-card {
  background: #ffffff !important;
  box-shadow: 0px 30px 30px rgba(106, 119, 132, 0.41) !important;
  border-radius: 23px !important;
}

.custom-card .v-card__text {
  width: 100%;
  padding: 37px 60px !important;
}

.field-block {
  margin-bottom: 16px;
}

.custom-field :deep(.v-input__details) {
  display: none !important;
}

.custom-field :deep(.v-label) {
  font-size: 14px !important;
}

.custom-field :deep(.v-input__control) {
  min-height: 40px !important;
}

.custom-field :deep(.v-input__slot) {
  min-height: 40px !important;
  background-color: white !important;
}

.custom-field :deep(.v-text-field__slot) {
  min-height: 40px !important;
}

.custom-field :deep(.v-text-field__slot input) {
  font-size: 14px !important;
  color: #666 !important;
  padding: 0 12px !important;
  height: 100% !important;
}

.custom-field.v-text-field--outlined :deep(fieldset) {
  border-color: #e0e0e0 !important;
}

@media (max-width: 600px) {
  .custom-card .v-card__text {
    padding: 16px !important;
  }
}
</style>

